// Типы элементов для рисовалки
export const ELEMENT_TYPES = {
  RECTANGLE: 'rectangle',
  CIRCLE: 'circle',
  ARROW: 'arrow',
  TEXT: 'text',
  // Basic Shapes
  TRIANGLE: 'triangle',
  DIAMOND: 'diamond',
  PARALLELOGRAM: 'parallelogram',
  ROUNDED_RECT: 'rounded_rect',
  ELLIPSE: 'ellipse',
  CYLINDER: 'cylinder',
  CLOUD: 'cloud',
  // Flowchart & Block Shapes
  DOCUMENT: 'document',
  DATABASE: 'database_shape', // Renamed to avoid confussion with isDatabase prop
  TERMINATOR: 'terminator',
  PREPARATION: 'preparation', // Hexagon usually
  MANUAL_INPUT: 'manual_input',
  MANUAL_OPERATION: 'manual_operation',
  DISPLAY: 'display',
  DATA: 'data', // Parallelogram often used for I/O
  DECISION: 'decision', // Diamond often used
  PROCESS: 'process', // Std rect
  INTERNAL_STORAGE: 'internal_storage',
  QUEUE: 'queue',
  L_RECT: 'l_rect',
  U_RECT: 'u_rect',
  TRAPEZOID: 'trapezoid',
  INV_TRAPEZOID: 'inv_trapezoid',
  STEP: 'step',
  CALLOUT: 'callout',
  ACTOR: 'actor',
  LOOP_LIMIT: 'loop_limit',
  STORAGE: 'storage',
  PACKAGE: 'package',
  USE_CASE: 'use_case', // Ellipse usually
  INTERFACE: 'interface',
  COMPONENT: 'component',
  ERD_CLASS: 'erd-class',
};

// Простая утилита логирования для отладки примагничивания (отключена)
const logSnap = () => {};

// Генератор уникальных ID
let idCounter = 0;
const generateUniqueId = () => {
  return `${Date.now()}.${++idCounter}.${Math.random().toString(36).substr(2, 9)}`;
};

// Создание базового элемента
export const createElement = (type, x, y, properties = {}) => ({
  id: generateUniqueId(),
  type,
  x,
  y,
  selected: false,
  ...properties
});

// Функция для получения точек подключения элемента (с отступом от блока для UI)
export const getConnectionPoints = (element, offset = 15) => {
  // ArchiMate элементы - используем стандартную логику для прямоугольников
  if (element.type === 'archimate' && element.width && element.height) {
    const w = element.width;
    const h = element.height;
    const x = element.x;
    const y = element.y;
    return {
      top: { x: x + w / 2, y: y - offset, side: 'top' },
      right: { x: x + w + offset, y: y + h / 2, side: 'right' },
      bottom: { x: x + w / 2, y: y + h + offset, side: 'bottom' },
      left: { x: x - offset, y: y + h / 2, side: 'left' }
    };
  }

  switch (element.type) {
    case ELEMENT_TYPES.RECTANGLE:
    case ELEMENT_TYPES.ROUNDED_RECT:
    case ELEMENT_TYPES.PARALLELOGRAM:
    case ELEMENT_TYPES.TRAPEZOID:
    case ELEMENT_TYPES.INV_TRAPEZOID:
    case ELEMENT_TYPES.TRIANGLE: // Simplified bbox connectivity
    case ELEMENT_TYPES.DIAMOND:
    case ELEMENT_TYPES.DECISION:
    case ELEMENT_TYPES.DOCUMENT:
    case ELEMENT_TYPES.DATA:
    case ELEMENT_TYPES.PREPARATION:
    case ELEMENT_TYPES.MANUAL_INPUT:
    case ELEMENT_TYPES.MANUAL_OPERATION:
    case ELEMENT_TYPES.DISPLAY:
    case ELEMENT_TYPES.INTERNAL_STORAGE:
    case ELEMENT_TYPES.QUEUE:
    case ELEMENT_TYPES.L_RECT:
    case ELEMENT_TYPES.U_RECT:
    case ELEMENT_TYPES.STEP:
    case ELEMENT_TYPES.CALLOUT:
    case ELEMENT_TYPES.ACTOR:
    case ELEMENT_TYPES.LOOP_LIMIT:
    case ELEMENT_TYPES.STORAGE:
    case ELEMENT_TYPES.PACKAGE:
    case ELEMENT_TYPES.COMPONENT:
    case ELEMENT_TYPES.TERMINATOR:
    case ELEMENT_TYPES.PROCESS:
    case ELEMENT_TYPES.CLOUD:
      // Для всех прямоугольнико-подобных - 4 основные точки с отступом от bounding box
      const w = element.width;
      const h = element.height;
      const x = element.x;
      const y = element.y;
      return {
        top: { x: x + w / 2, y: y - offset },
        right: { x: x + w + offset, y: y + h / 2 },
        bottom: { x: x + w / 2, y: y + h + offset },
        left: { x: x - offset, y: y + h / 2 }
      };
    case ELEMENT_TYPES.CYLINDER:
    case ELEMENT_TYPES.DATABASE:
      // Для цилиндра и базы данных:
      // Top: верхняя точка эллипса (element.y)
      // Bottom: нижняя плоская грань тела (element.y + h - ry), откуда обычно рисуется lifeline
      // Left/Right: середина видимого тела
      const wc = element.width;
      const hc = element.height;
      const ry = Math.min(hc * 0.15, wc * 0.2);
      const midY = element.y + ry + (hc - 2 * ry) / 2;
      return {
        top: { x: element.x + wc / 2, y: element.y },
        right: { x: element.x + wc, y: midY },
        bottom: { x: element.x + wc / 2, y: element.y + hc - ry },
        left: { x: element.x, y: midY }
      };
    case ELEMENT_TYPES.CIRCLE:
    case ELEMENT_TYPES.ELLIPSE:
    case ELEMENT_TYPES.USE_CASE:
    case ELEMENT_TYPES.INTERFACE:
      const centerX = element.x + (element.radius || element.width / 2);
      const centerY = element.y + (element.radius || element.height / 2);
      // Если это круг (radius), то r = radius. Если эллипс (w/h), то берем аппроксимацию или bounding box logic.
      // Для простоты используем bbox логику для Ellipse тоже, если свойства radius нет.
      if (element.radius) {
          const r = element.radius + offset;
          return {
            top: { x: centerX, y: centerY - r },
            right: { x: centerX + r, y: centerY },
            bottom: { x: centerX, y: centerY + r },
            left: { x: centerX - r, y: centerY }
          };
      } else {
          // Fallback for Ellipse defined by width/height
          return {
            top: { x: centerX, y: element.y - offset },
            right: { x: element.x + element.width + offset, y: centerY },
            bottom: { x: centerX, y: element.y + element.height + offset },
            left: { x: element.x - offset, y: centerY }
          };
      }
    case ELEMENT_TYPES.ARROW:
      // lifeline вертикальная пунктирная линия – даём точки подключения
      if (element.lifeline) {
        const midX = (element.x + element.x2) / 2;
        const midY = (element.y + element.y2) / 2;
        return {
          top: { x: element.x, y: element.y },
          mid: { x: midX, y: midY },
          bottom: { x: element.x2, y: element.y2 }
        };
      }
      return {};
    default: {
      // Fallback: для неизвестных типов с прямоугольным bbox возвращаем стандартные 4 точки
      if (
        typeof element.x === 'number' &&
        typeof element.y === 'number' &&
        typeof element.width === 'number' &&
        typeof element.height === 'number'
      ) {
        const w = element.width;
        const h = element.height;
        const x = element.x;
        const y = element.y;
        return {
          top: { x: x + w / 2, y: y - offset },
          right: { x: x + w + offset, y: y + h / 2 },
          bottom: { x: x + w / 2, y: y + h + offset },
          left: { x: x - offset, y: y + h / 2 }
        };
      }
      return {};
    }
  }
};

// Функция для получения точки на границе элемента (для рисования стрелок - без отступа)
export const getEdgePoint = (element, side) => {
  // ArchiMate элементы - используем стандартную логику для прямоугольников
  if (element.type === 'archimate' && element.width && element.height) {
    const w = element.width;
    const h = element.height;
    const x = element.x;
    const y = element.y;
    switch (side) {
      case 'top': return { x: x + w / 2, y };
      case 'right': return { x: x + w, y: y + h / 2 };
      case 'bottom': return { x: x + w / 2, y: y + h };
      case 'left': return { x, y: y + h / 2 };
      default: return { x: x + w / 2, y: y + h / 2 };
    }
  }

  switch (element.type) {
    case ELEMENT_TYPES.RECTANGLE:
    case ELEMENT_TYPES.ROUNDED_RECT:
    case ELEMENT_TYPES.PARALLELOGRAM:
    case ELEMENT_TYPES.TRAPEZOID:
    case ELEMENT_TYPES.INV_TRAPEZOID:
    case ELEMENT_TYPES.TRIANGLE:
    case ELEMENT_TYPES.DIAMOND:
    case ELEMENT_TYPES.DECISION:
    case ELEMENT_TYPES.DOCUMENT:
    case ELEMENT_TYPES.CYLINDER:
    case ELEMENT_TYPES.DATABASE:
    case ELEMENT_TYPES.DATA:
    case ELEMENT_TYPES.PREPARATION:
    case ELEMENT_TYPES.MANUAL_INPUT:
    case ELEMENT_TYPES.MANUAL_OPERATION:
    case ELEMENT_TYPES.DISPLAY:
    case ELEMENT_TYPES.INTERNAL_STORAGE:
    case ELEMENT_TYPES.QUEUE:
    case ELEMENT_TYPES.L_RECT:
    case ELEMENT_TYPES.U_RECT:
    case ELEMENT_TYPES.STEP:
    case ELEMENT_TYPES.CALLOUT:
    case ELEMENT_TYPES.ACTOR:
    case ELEMENT_TYPES.LOOP_LIMIT:
    case ELEMENT_TYPES.STORAGE:
    case ELEMENT_TYPES.PACKAGE:
    case ELEMENT_TYPES.COMPONENT:
    case ELEMENT_TYPES.TERMINATOR:
    case ELEMENT_TYPES.PROCESS:
    case ELEMENT_TYPES.CLOUD:
    case ELEMENT_TYPES.ELLIPSE:
    case ELEMENT_TYPES.USE_CASE:
      const w = element.width;
      const h = element.height;
      const x = element.x;
      const y = element.y;
      switch (side) {
        case 'top': return { x: x + w / 2, y };
        case 'right': return { x: x + w, y: y + h / 2 };
        case 'bottom': return { x: x + w / 2, y: y + h };
        case 'left': return { x, y: y + h / 2 };
        default: return { x: x + w / 2, y: y + h / 2 };
      }
    case ELEMENT_TYPES.CIRCLE:
    case ELEMENT_TYPES.INTERFACE:
      const centerX = element.x + element.radius;
      const centerY = element.y + element.radius;
      const r = element.radius;
      switch (side) {
        case 'top': return { x: centerX, y: centerY - r };
        case 'right': return { x: centerX + r, y: centerY };
        case 'bottom': return { x: centerX, y: centerY + r };
        case 'left': return { x: centerX - r, y: centerY };
        default: return { x: centerX, y: centerY };
      }
    default:
      // Fallback: если у элемента есть bbox, ведём себя как для прямоугольника
      if (
        typeof element.x === 'number' &&
        typeof element.y === 'number' &&
        typeof element.width === 'number' &&
        typeof element.height === 'number'
      ) {
        const w = element.width;
        const h = element.height;
        const x = element.x;
        const y = element.y;
        switch (side) {
          case 'top': return { x: x + w / 2, y };
          case 'right': return { x: x + w, y: y + h / 2 };
          case 'bottom': return { x: x + w / 2, y: y + h };
          case 'left': return { x, y: y + h / 2 };
          default: return { x: x + w / 2, y: y + h / 2 };
        }
      }
      return { x: element.x, y: element.y };
  }
};

// Функция для поиска ближайшей точки подключения
export const findNearestConnectionPoint = (x, y, elements, excludeElement = null, maxDistance = 50) => {
  let minDistance = maxDistance; // максимальное расстояние для притягивания
  let nearestPoint = null;
  let targetElement = null;
  const candidates = [];

  elements.forEach(element => {
    if (
      element === excludeElement ||
      element.type === ELEMENT_TYPES.TEXT ||
      // Исключаем обычные стрелки, но разрешаем lifeline-arrow
      (element.type === ELEMENT_TYPES.ARROW && !element.lifeline) ||
      element.connectable === false
    ) return;

    // Проверяем близость к элементу
    let cx, cy;
    if (element.type === ELEMENT_TYPES.CIRCLE || (element.radius && !element.width)) {
      cx = element.x + element.radius;
      cy = element.y + element.radius;
    } else {
      cx = element.x + element.width / 2;
      cy = element.y + element.height / 2;
    }

    const distToCenter = Math.hypot(x - cx, y - cy);
    const maxRadius = (element.type === ELEMENT_TYPES.CIRCLE || element.radius)
      ? element.radius + maxDistance
      : Math.hypot(element.width / 2, element.height / 2) + maxDistance;

    if (distToCenter > maxRadius) return;

    // Определяем лучшую сторону для подключения
    const dx = x - cx;
    const dy = y - cy;

    let bestSide;
    if (Math.abs(dx) > Math.abs(dy)) {
      bestSide = dx > 0 ? 'right' : 'left';
    } else {
      bestSide = dy > 0 ? 'bottom' : 'top';
    }

    const points = getConnectionPoints(element);
    const point = points[bestSide];

    if (point) {
      const distance = Math.hypot(x - point.x, y - point.y);
      candidates.push({ elementId: element.id, type: element.type, side: bestSide, distance });
      if (distance < minDistance) {
        minDistance = distance;
        nearestPoint = { ...point, side: bestSide };
        targetElement = element;
      }
    }
  });

  if (nearestPoint) {
    logSnap('query', { x, y, maxDistance }, 'chosen', { elementId: targetElement.id, side: nearestPoint.side, distance: minDistance });
  } else {
    logSnap('query', { x, y, maxDistance }, 'no-hit', { candidates: candidates.length });
  }

  return nearestPoint ? { point: nearestPoint, element: targetElement, distance: minDistance } : null;
};

// Функция для создания ElementFactory с учетом темы
export const createThemedElementFactory = (theme) => {
  const elementColors = theme?.elements || {};

  const createGenericShape = (type, x, y, w = 100, h = 60, customProps = {}) => createElement(type, x, y, {
      width: w,
      height: h,
      fill: elementColors.rectangle?.fill || '#ffffff',
      stroke: elementColors.rectangle?.stroke || '#000000',
      strokeWidth: elementColors.rectangle?.strokeWidth || 2,
      ...customProps
  });

  return {
    rectangle: (x, y, width = 100, height = 60, props = {}) => createGenericShape(ELEMENT_TYPES.RECTANGLE, x, y, width, height, props),

    // Generic factories for new shapes
    [ELEMENT_TYPES.TRIANGLE]: (x, y) => createGenericShape(ELEMENT_TYPES.TRIANGLE, x, y, 100, 100),
    [ELEMENT_TYPES.DIAMOND]: (x, y) => createGenericShape(ELEMENT_TYPES.DIAMOND, x, y, 100, 100),
    [ELEMENT_TYPES.PARALLELOGRAM]: (x, y) => createGenericShape(ELEMENT_TYPES.PARALLELOGRAM, x, y, 120, 60),
    [ELEMENT_TYPES.ROUNDED_RECT]: (x, y) => createGenericShape(ELEMENT_TYPES.ROUNDED_RECT, x, y, 120, 60, { borderRadius: 10 }),
    [ELEMENT_TYPES.ELLIPSE]: (x, y) => createGenericShape(ELEMENT_TYPES.ELLIPSE, x, y, 120, 80),
    [ELEMENT_TYPES.CYLINDER]: (x, y) => createGenericShape(ELEMENT_TYPES.CYLINDER, x, y, 80, 100),
    [ELEMENT_TYPES.CLOUD]: (x, y) => createGenericShape(ELEMENT_TYPES.CLOUD, x, y, 120, 80),
    [ELEMENT_TYPES.DOCUMENT]: (x, y) => createGenericShape(ELEMENT_TYPES.DOCUMENT, x, y, 100, 80),
    [ELEMENT_TYPES.DATABASE]: (x, y) => createGenericShape(ELEMENT_TYPES.DATABASE, x, y, 80, 100),
    [ELEMENT_TYPES.TERMINATOR]: (x, y) => createGenericShape(ELEMENT_TYPES.TERMINATOR, x, y, 120, 50, { borderRadius: 25 }),
    [ELEMENT_TYPES.PREPARATION]: (x, y) => createGenericShape(ELEMENT_TYPES.PREPARATION, x, y, 120, 60),
    [ELEMENT_TYPES.MANUAL_INPUT]: (x, y) => createGenericShape(ELEMENT_TYPES.MANUAL_INPUT, x, y, 120, 60),
    [ELEMENT_TYPES.MANUAL_OPERATION]: (x, y) => createGenericShape(ELEMENT_TYPES.MANUAL_OPERATION, x, y, 120, 60),
    [ELEMENT_TYPES.DISPLAY]: (x, y) => createGenericShape(ELEMENT_TYPES.DISPLAY, x, y, 120, 80),
    [ELEMENT_TYPES.DATA]: (x, y) => createGenericShape(ELEMENT_TYPES.DATA, x, y, 120, 60),
    [ELEMENT_TYPES.DECISION]: (x, y) => createGenericShape(ELEMENT_TYPES.DECISION, x, y, 100, 80),
    [ELEMENT_TYPES.PROCESS]: (x, y) => createGenericShape(ELEMENT_TYPES.PROCESS, x, y, 100, 60),
    [ELEMENT_TYPES.INTERNAL_STORAGE]: (x, y) => createGenericShape(ELEMENT_TYPES.INTERNAL_STORAGE, x, y, 120, 80),
    [ELEMENT_TYPES.QUEUE]: (x, y) => createGenericShape(ELEMENT_TYPES.QUEUE, x, y, 100, 60),
    [ELEMENT_TYPES.L_RECT]: (x, y) => createGenericShape(ELEMENT_TYPES.L_RECT, x, y, 100, 100),
    [ELEMENT_TYPES.U_RECT]: (x, y) => createGenericShape(ELEMENT_TYPES.U_RECT, x, y, 100, 100),
    [ELEMENT_TYPES.TRAPEZOID]: (x, y) => createGenericShape(ELEMENT_TYPES.TRAPEZOID, x, y, 120, 60),
    [ELEMENT_TYPES.INV_TRAPEZOID]: (x, y) => createGenericShape(ELEMENT_TYPES.INV_TRAPEZOID, x, y, 120, 60),
    [ELEMENT_TYPES.STEP]: (x, y) => createGenericShape(ELEMENT_TYPES.STEP, x, y, 100, 60),
    [ELEMENT_TYPES.CALLOUT]: (x, y) => createGenericShape(ELEMENT_TYPES.CALLOUT, x, y, 100, 80),
    [ELEMENT_TYPES.ACTOR]: (x, y) => createGenericShape(ELEMENT_TYPES.ACTOR, x, y, 60, 100),
    [ELEMENT_TYPES.LOOP_LIMIT]: (x, y) => createGenericShape(ELEMENT_TYPES.LOOP_LIMIT, x, y, 120, 60),
    [ELEMENT_TYPES.STORAGE]: (x, y) => createGenericShape(ELEMENT_TYPES.STORAGE, x, y, 120, 80),
    [ELEMENT_TYPES.PACKAGE]: (x, y) => createGenericShape(ELEMENT_TYPES.PACKAGE, x, y, 120, 80),
    [ELEMENT_TYPES.USE_CASE]: (x, y) => createGenericShape(ELEMENT_TYPES.USE_CASE, x, y, 140, 80),
    [ELEMENT_TYPES.INTERFACE]: (x, y) => createElement(ELEMENT_TYPES.INTERFACE, x, y, { radius: 20 }),
    [ELEMENT_TYPES.COMPONENT]: (x, y) => createGenericShape(ELEMENT_TYPES.COMPONENT, x, y, 140, 80),

    // Новый тип: база данных — теперь используем тип CYLINDER для лучшего визуального отображения
    database: (x, y, width = 120, height = 40, props = {}) => createElement(ELEMENT_TYPES.CYLINDER, x, y, {
      width,
      height,
      isDatabase: true,
      fill: elementColors.database?.fill || elementColors.rectangle?.fill || '#ffffff',
      stroke: elementColors.database?.stroke || elementColors.rectangle?.stroke || '#000000',
      strokeWidth: elementColors.database?.strokeWidth || elementColors.rectangle?.strokeWidth || 2,
      ...props
    }),

    circle: (x, y, radius = 30, props = {}) => createElement(ELEMENT_TYPES.CIRCLE, x, y, {
      radius,
      fill: elementColors.circle?.fill || '#ffffff',
      stroke: elementColors.circle?.stroke || '#000000',
      strokeWidth: elementColors.circle?.strokeWidth || 2,
      ...props
    }),

    arrow: (x1, y1, x2, y2, startConnection = null, endConnection = null) => createElement(ELEMENT_TYPES.ARROW, x1, y1, {
      x2,
      y2,
      stroke: elementColors.arrow?.stroke || '#000000',
      strokeWidth: elementColors.arrow?.strokeWidth || 2,
      arrowSize: 10,
      startConnection, // { elementId, side }
      endConnection   // { elementId, side }
    }),

    text: (x, y, content = 'Текст') => createElement(ELEMENT_TYPES.TEXT, x, y, {
      content,
      fontSize: 16,
      fontFamily: 'Arial',
      fill: elementColors.text?.fill || '#000000'
    }),

    note: (x, y, width = 160, height = 80, props = {}) => createElement(ELEMENT_TYPES.RECTANGLE, x, y, {
      width,
      height,
      isNote: true,
      fill: elementColors.note?.fill || '#fffbe6',
      stroke: elementColors.note?.stroke || '#d4b106',
      strokeWidth: elementColors.note?.strokeWidth || 2,
      ...props
    }),

    /** Creates an ArchiMate element (rendered by ArchiMateElementRenderer). */
    archimate: (archimateType, x, y, width = 160, height = 80, label = '') =>
      createElement('archimate', x, y, { archimateType, content: label, width, height }),
  };
};

// Оригинальный ElementFactory для обратной совместимости (будет использовать дефолтные цвета)
export const ElementFactory = createThemedElementFactory({});

// Helper to identify standard shapes (not lines/text)
export const isShape = (element) => {
  return element.type !== ELEMENT_TYPES.ARROW && element.type !== ELEMENT_TYPES.TEXT;
};

